/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import { useCallback, useEffect, useState } from "react";

import {
  MediaStreamType,
  MediaType,
  PlayerPlayInfo,
} from "../../../shared/types";
import { usePlayerSettings } from "../../BasePlayer";
import { useCustomPlayerContext } from "../../CustomPlayerContext";

import { assetTimestampToSeconds } from "./helpers";

interface useStartPositionLoaderParams {
  streamType: MediaStreamType;
  playInfo: PlayerPlayInfo;
  enabled: boolean;
}

export const useStartPositionLoader = (
  params: useStartPositionLoaderParams,
) => {
  const {
    streamType,
    playInfo: {
      Timestamp: resumeTimestamp,
      MediaId: mediaId,
      MediaTypeCode: mediaType,
    },
    enabled,
  } = params;

  const [startPosition, setStartPosition] = useState<number>(0);
  const [loaded, setLoaded] = useState<boolean>(false);

  const { onLoadStartPosition } = useCustomPlayerContext();

  const {
    continueWatching: { resumeSupportingMedia },
  } = usePlayerSettings();

  const isContinueWatchingResumeAvailable = useCallback(
    (mediaType: string, streamType: MediaStreamType): boolean => {
      const isTrialStream = streamType === MediaStreamType.Trial;

      const isSupportedMediaType = resumeSupportingMedia.includes(
        mediaType as MediaType,
      );

      return isSupportedMediaType && !isTrialStream;
    },
    [resumeSupportingMedia],
  );

  const load = useCallback(async (): Promise<number> => {
    if (
      enabled &&
      mediaType &&
      isContinueWatchingResumeAvailable(mediaType, streamType)
    ) {
      // Use resume timestamp from media info response if available
      // Anonymous user does not send continue watching requests to API, so
      // this timestamp will be available only for authorized users
      if (resumeTimestamp) {
        return assetTimestampToSeconds(resumeTimestamp);
      }

      if (onLoadStartPosition && mediaId) {
        const position = await onLoadStartPosition(mediaId);
        if (position !== undefined && position > 0) {
          return position;
        }
      }
    }

    return 0;
  }, [
    enabled,
    mediaId,
    mediaType,
    onLoadStartPosition,
    resumeTimestamp,
    streamType,
  ]);

  useEffect(() => {
    let mounted = true;

    load()
      .then((position: number) => mounted && setStartPosition(position))
      .finally(() => mounted && setLoaded(true));

    return () => {
      mounted = false;
    };
  }, [load]);

  return {
    startPosition,
    loaded,
  };
};
